import { Button } from "@/components/ui/button";
import { ArrowLeft, Trophy, Hash, Target, TrendingUp, TrendingDown, Calendar, Loader2 } from "lucide-react";
import { useLocation, useParams } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import "./MatchSummaryPage.css";

interface RatingSnapshot {
  before: number;
  after: number;
  delta: number;
}

interface LeagueMatchDetail {
  id: string;
  leagueId: string;
  leagueName?: string | null;
  status: string;
  playedAt?: string;
  winner?: number;
  score?: any;
  team1Player1Id: string;
  team1Player2Id?: string | null;
  team2Player1Id: string;
  team2Player2Id?: string | null;
  team1Player1Name?: string | null;
  team1Player2Name?: string | null;
  team2Player1Name?: string | null;
  team2Player2Name?: string | null;
  team1Player1Image?: string | null;
  team1Player2Image?: string | null;
  team2Player1Image?: string | null;
  team2Player2Image?: string | null;
  ratingChanges?: {
    team1Player1?: RatingSnapshot;
    team1Player2?: RatingSnapshot;
    team2Player1?: RatingSnapshot;
    team2Player2?: RatingSnapshot;
  };
}

interface MatchHistoryItem {
  id: string;
  type: 'league' | 'global';
  leagueId?: string;
  leagueName?: string;
  homeClubId?: string | null;
  homeClubName?: string | null;
  playedAt: string;
  status: string;
  winner: number;
  score: any;
  userTeam: number;
  userWon: boolean;
  ratingBefore: number;
  ratingAfter: number;
  ratingDelta: number;
  opponents: string[];
  teammate?: string;
  expectedProbability?: number;
  playerImage1?: string;
  playerImage2?: string;
  playerImage3?: string;
  playerImage4?: string;
}

function getScoreSets(score: any): Array<{ team1: number; team2: number }> {
  if (typeof score === 'string') {
    return score.split(',').map(s => {
      const [team1, team2] = s.trim().split('-').map(Number);
      return { team1: team1 || 0, team2: team2 || 0 };
    });
  }
  if (score?.sets && Array.isArray(score.sets)) {
    return score.sets;
  }
  return [];
}

function formatDateTime(value: string): string {
  try {
    const d = new Date(value);
    return (
      d.toLocaleDateString('es-DO', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }) +
      ' · ' +
      d.toLocaleTimeString('es-DO', {
        hour: '2-digit',
        minute: '2-digit',
      })
    );
  } catch {
    return value;
  }
}

export function MatchSummaryPage() {
  const [, setLocation] = useLocation();
  const params = useParams<{ matchId: string }>();
  const { user } = useAuth();
  const matchId = params.matchId;

  const { data: matchHistory = [], isLoading: isHistoryLoading } = useQuery<MatchHistoryItem[]>({
    queryKey: ['/api/profile/match-history'],
  });

  const matchFromHistory = matchHistory.find(m => m.id === matchId);

  // Fallback: when the viewer didn't play the match (e.g. an admin opening
  // a match between other members from the league's "Partidos" tab), the
  // viewer-centric history endpoint won't return it. Fetch the match
  // directly by id from the league endpoint and project it into the same
  // shape the rest of the page already consumes.
  const fallbackEnabled = !isHistoryLoading && !matchFromHistory && !!matchId;
  const {
    data: leagueMatch,
    isLoading: isLeagueMatchLoading,
    error: leagueMatchError,
  } = useQuery<LeagueMatchDetail>({
    queryKey: ['/api/leagues/matches', matchId],
    enabled: fallbackEnabled,
    retry: false,
  });
  const isForbidden = leagueMatchError instanceof Error && leagueMatchError.message.startsWith("403");

  const fallbackMatch = useMemo<MatchHistoryItem | null>(() => {
    if (!leagueMatch || !user) return null;
    const userId = user.id;
    const isOnTeam1 = userId === leagueMatch.team1Player1Id || userId === leagueMatch.team1Player2Id;
    const isOnTeam2 = userId === leagueMatch.team2Player1Id || userId === leagueMatch.team2Player2Id;
    const userTeam = isOnTeam1 ? 1 : isOnTeam2 ? 2 : 0;
    const userWon = userTeam !== 0 && leagueMatch.winner === userTeam;

    const rc = leagueMatch.ratingChanges;
    // Pick the rating snapshot for the exact slot the viewer occupies so
    // delta/before/after reflect the viewer (not just team1Player1 by default).
    const mySlot =
      userId === leagueMatch.team1Player1Id
        ? rc?.team1Player1
        : userId === leagueMatch.team1Player2Id
          ? rc?.team1Player2
          : userId === leagueMatch.team2Player1Id
            ? rc?.team2Player1
            : userId === leagueMatch.team2Player2Id
              ? rc?.team2Player2
              : undefined;
    const myRating = mySlot;

    const t1p1 = rc?.team1Player1?.before;
    const t1p2 = rc?.team1Player2?.before;
    const t2p1 = rc?.team2Player1?.before;
    const t2p2 = rc?.team2Player2?.before;
    let expectedProbability: number | undefined;
    if (typeof t1p1 === 'number' && typeof t2p1 === 'number') {
      const avg1 = typeof t1p2 === 'number' ? (t1p1 + t1p2) / 2 : t1p1;
      const avg2 = typeof t2p2 === 'number' ? (t2p1 + t2p2) / 2 : t2p1;
      expectedProbability = Math.round((1 / (1 + Math.pow(10, (avg2 - avg1) / 400))) * 100);
    }

    // Build opponents/teammate from the viewer's perspective. When the
    // viewer isn't a participant, surface team 1's pair as "opponents[0..1]"
    // and team 2's pair as "opponents[2..3]" — the existing renderer reads
    // those slots to label both rows when viewerInMatch is false.
    let opponents: string[] = [];
    let teammate: string | undefined;
    if (isOnTeam1) {
      opponents = [leagueMatch.team2Player1Name ?? '', leagueMatch.team2Player2Name ?? ''].filter(Boolean);
      teammate = userId === leagueMatch.team1Player1Id
        ? (leagueMatch.team1Player2Name ?? undefined)
        : (leagueMatch.team1Player1Name ?? undefined);
    } else if (isOnTeam2) {
      opponents = [leagueMatch.team1Player1Name ?? '', leagueMatch.team1Player2Name ?? ''].filter(Boolean);
      teammate = userId === leagueMatch.team2Player1Id
        ? (leagueMatch.team2Player2Name ?? undefined)
        : (leagueMatch.team2Player1Name ?? undefined);
    } else {
      opponents = [
        leagueMatch.team1Player1Name ?? '',
        leagueMatch.team1Player2Name ?? '',
        leagueMatch.team2Player1Name ?? '',
        leagueMatch.team2Player2Name ?? '',
      ];
    }

    return {
      id: leagueMatch.id,
      type: 'league',
      leagueId: leagueMatch.leagueId,
      leagueName: leagueMatch.leagueName ?? undefined,
      playedAt: leagueMatch.playedAt ?? new Date().toISOString(),
      status: leagueMatch.status ?? 'approved',
      winner: leagueMatch.winner ?? 0,
      score: leagueMatch.score,
      userTeam,
      userWon,
      ratingBefore: myRating?.before ?? 0,
      ratingAfter: myRating?.after ?? 0,
      ratingDelta: myRating?.delta ?? 0,
      opponents,
      teammate,
      expectedProbability,
      playerImage1: leagueMatch.team1Player1Image ?? undefined,
      playerImage2: leagueMatch.team1Player2Image ?? undefined,
      playerImage3: leagueMatch.team2Player1Image ?? undefined,
      playerImage4: leagueMatch.team2Player2Image ?? undefined,
    };
  }, [leagueMatch, user]);

  const match = matchFromHistory ?? fallbackMatch;
  const isLoading =
    isHistoryLoading || (fallbackEnabled && isLeagueMatchLoading && !fallbackMatch);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!match) {
    return (
      <div className="min-h-screen bg-background">
        <header
          className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          <div className="container max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setLocation("/")}
              data-testid="button-back-match-summary"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Resumen del Partido
            </h1>
          </div>
        </header>
        <main className="container max-w-lg mx-auto px-4 py-8 text-center">
          <p className="text-muted-foreground" data-testid="text-match-summary-error">
            {isForbidden
              ? "No tienes acceso a este partido."
              : "No se encontró el partido solicitado."}
          </p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => setLocation("/")}
          >
            Volver al inicio
          </Button>
        </main>
      </div>
    );
  }

  const sets = getScoreSets(match.score);

  // Build user vs rival team views from existing data shape.
  const userName = user?.firstName || "Tú";
  const userTeamData = { p1Name: userName, p2Name: match.teammate || null };
  const rivalTeamData = {
    p1Name: match.opponents[0] || "—",
    p2Name: match.opponents[1] || null,
  };

  // When viewer isn't in the match (userTeam === 0) the page still works,
  // we just label the teams generically and skip rating chips.
  const viewerInMatch = match.userTeam === 1 || match.userTeam === 2;
  const userIsT1 = match.userTeam === 1;

  // When the viewer isn't in the match we don't currently have a reliable
  // way to split the 4 players into two teams from this endpoint, so we
  // surface the available names on the first row and leave the second
  // row empty rather than guess. (history is viewer-centric in practice.)
  const userTeam = viewerInMatch
    ? userTeamData
    : { p1Name: match.opponents[0] || "—", p2Name: match.opponents[1] || null };
  const rivalTeam = viewerInMatch
    ? rivalTeamData
    : { p1Name: match.opponents[2] || "—", p2Name: match.opponents[3] || null };

  // Aggregate game totals from "user" perspective (or team1 perspective if not in match).
  const totals = sets.reduce(
    (acc, s) => {
      if (viewerInMatch) {
        acc.user += userIsT1 ? s.team1 : s.team2;
        acc.rival += userIsT1 ? s.team2 : s.team1;
      } else {
        acc.user += s.team1;
        acc.rival += s.team2;
      }
      return acc;
    },
    { user: 0, rival: 0 },
  );
  const userGames = totals.user;
  const rivalGames = totals.rival;
  const totalGames = userGames + rivalGames;
  const userPct = totalGames > 0 ? Math.round((userGames / totalGames) * 100) : 0;
  const rivalPct = totalGames > 0 ? 100 - userPct : 0;

  const probUser = match.expectedProbability ?? 50;
  const probRival = 100 - probUser;

  const isPending = match.status === "pending";

  // Rating chip per team. Zero-sum: winner +abs, loser -abs.
  // Only on confirmed/history matches (parity with mobile/mockup).
  const showRatingChips = !isPending && viewerInMatch && match.ratingDelta !== 0;
  const absDelta = Math.abs(match.ratingDelta);
  const userIsWinner = userGames > rivalGames;


  // Labels for team rows.
  const userLabel = viewerInMatch ? "TÚ Y TU PAREJA" : "EQUIPO 1";
  const rivalLabel = viewerInMatch ? "RIVALES" : "EQUIPO 2";

  return (
    <div
      className="flex flex-col bg-background overflow-hidden cv-summary-root"
      style={{ height: '100dvh' }}
    >
      <header className="flex-shrink-0 bg-background/95 backdrop-blur border-b z-50 safe-area-top">
        <div className="container max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/")}
            data-testid="button-back-match-summary"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            Resumen del Partido
          </h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto overscroll-contain">
        <div className="container max-w-lg mx-auto cv-summary-content">

          {/* Match Context */}
          <div className="flex justify-between items-start gap-3">
            <div className="min-w-0">
              <div className="text-lg font-extrabold mb-1.5 truncate" data-testid="text-match-league">
                {match.leagueName || (match.type === 'global' ? "Partido Global" : "Partido Libre")}
              </div>
              {match.homeClubName ? (
                <div
                  className="text-xs mb-1 truncate"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid="text-match-home-club"
                >
                  {match.homeClubName}
                </div>
              ) : null}
              <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                <Calendar size={14} />
                <span className="cv-summary-mono" data-testid="text-match-date">
                  {formatDateTime(match.playedAt)}
                </span>
              </div>
            </div>
            <div className={`cv-summary-tag ${isPending ? 'cv-summary-tag-pending' : 'cv-summary-tag-confirmed'}`} data-testid="badge-match-status">
              {isPending ? "Pendiente" : "Confirmado"}
            </div>
          </div>

          {/* The Result - Asymmetric Table */}
          <div>
            <div className="cv-summary-section-title">
              <Hash size={14} /> Marcador Final
            </div>
            <div className="cv-summary-panel" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="cv-summary-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', paddingLeft: 12, paddingTop: 12 }}>Equipo</th>
                    {sets.map((_, i) => (
                      <th key={i} style={{ paddingTop: 12 }}>S{i + 1}</th>
                    ))}
                    <th style={{ color: 'var(--user-accent)', paddingTop: 12, paddingRight: 12 }}>TOT</th>
                  </tr>
                </thead>
                <tbody>
                  {/* User Row */}
                  <tr className="user-row" data-testid="row-team-user">
                    <td style={{ textAlign: 'left', padding: '14px 12px' }}>
                      <div className="cv-summary-team-label" style={{ color: 'var(--user-accent)' }}>{userLabel}</div>
                      <div className="cv-summary-team-name">{userTeam.p1Name}</div>
                      {userTeam.p2Name && <div className="cv-summary-team-name">{userTeam.p2Name}</div>}
                      {showRatingChips && (
                        <div className={`cv-summary-rating-chip ${userIsWinner ? 'win' : 'loss'}`} data-testid="chip-rating-user">
                          {userIsWinner ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {userIsWinner ? '+' : '−'}{absDelta}
                        </div>
                      )}
                    </td>
                    {sets.map((s, i) => {
                      const uScore = viewerInMatch ? (userIsT1 ? s.team1 : s.team2) : s.team1;
                      const rScore = viewerInMatch ? (userIsT1 ? s.team2 : s.team1) : s.team2;
                      return (
                        <td key={i} className={`cv-summary-mono ${uScore > rScore ? 'score-win' : ''}`}>
                          {uScore}
                        </td>
                      );
                    })}
                    <td className="cv-summary-mono score-total" style={{ color: 'var(--user-accent)', paddingRight: 12 }}>{userGames}</td>
                  </tr>

                  {/* Rival Row */}
                  <tr data-testid="row-team-rival">
                    <td style={{ textAlign: 'left', padding: '14px 12px' }}>
                      <div className="cv-summary-team-label" style={{ color: 'var(--text-secondary)' }}>{rivalLabel}</div>
                      <div className="cv-summary-team-name" style={{ color: 'var(--text-secondary)' }}>{rivalTeam.p1Name}</div>
                      {rivalTeam.p2Name && <div className="cv-summary-team-name" style={{ color: 'var(--text-secondary)' }}>{rivalTeam.p2Name}</div>}
                      {showRatingChips && (
                        <div className={`cv-summary-rating-chip ${userIsWinner ? 'loss' : 'win'}`} data-testid="chip-rating-rival">
                          {userIsWinner ? <TrendingDown size={12} /> : <TrendingUp size={12} />}
                          {userIsWinner ? '−' : '+'}{absDelta}
                        </div>
                      )}
                    </td>
                    {sets.map((s, i) => {
                      const uScore = viewerInMatch ? (userIsT1 ? s.team1 : s.team2) : s.team1;
                      const rScore = viewerInMatch ? (userIsT1 ? s.team2 : s.team1) : s.team2;
                      return (
                        <td key={i} className={`cv-summary-mono ${rScore > uScore ? 'score-win' : ''}`}>
                          {rScore}
                        </td>
                      );
                    })}
                    <td className="cv-summary-mono score-total" style={{ color: 'var(--text-secondary)', paddingRight: 12 }}>{rivalGames}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Expectations vs Reality */}
          <div>
            <div className="cv-summary-section-title">
              <Target size={14} /> Predicción vs Realidad
            </div>
            <div className="cv-summary-panel">
              <div className="cv-summary-bar-wrap">
                <div className="cv-summary-bar-labels">
                  <span style={{ color: 'var(--user-accent)', fontWeight: 600 }}>
                    {viewerInMatch ? 'TU CHANCE' : 'EQUIPO 1'}: <span className="cv-summary-mono">{probUser}%</span>
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {viewerInMatch ? 'RIVAL' : 'EQUIPO 2'}: <span className="cv-summary-mono">{probRival}%</span>
                  </span>
                </div>
                <div className="cv-summary-bar">
                  <div className="cv-summary-bar-fill-user" style={{ width: `${probUser}%` }}></div>
                </div>
                <div className="cv-summary-bar-caption">Probabilidad antes del partido</div>
              </div>

              <div style={{ height: 1, background: 'var(--border-subtle)', margin: '16px 0', opacity: 0.6 }}></div>

              <div className="cv-summary-bar-wrap">
                <div className="cv-summary-bar-labels">
                  <span style={{ color: 'var(--user-accent)', fontWeight: 600 }}>
                    {viewerInMatch ? 'DOMINIO (TÚ)' : 'EQUIPO 1'}: <span className="cv-summary-mono">{userPct}%</span>
                  </span>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {viewerInMatch ? 'RIVAL' : 'EQUIPO 2'}: <span className="cv-summary-mono">{rivalPct}%</span>
                  </span>
                </div>
                <div className="cv-summary-bar">
                  <div className="cv-summary-bar-fill-user" style={{ width: `${userPct}%` }}></div>
                </div>
                <div className="cv-summary-bar-caption">Distribución de games ganados</div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}

export default MatchSummaryPage;
